import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Users, Monitor, RefreshCw, Crown, Medal, Lock } from "lucide-react";
import { API } from "../App";
import axios from "axios";

export default function LeaderboardPage() {
  const { competitionId } = useParams();
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const fetchLiveData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/live/${competitionId}`);
      
      // Check if audience voting is enabled - if not, deny access
      if (!res.data.competition?.audience_voting_enabled) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      
      setLiveData(res.data);
      setAccessDenied(false);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching live data:", error);
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 3000); // Refresh every 3 seconds
    return () => clearInterval(interval);
  }, [fetchLiveData]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Access denied if audience voting not enabled
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#27272a] rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-[#52525B]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-3">
            Live Scores Not Available
          </h1>
          <p className="text-[#A1A1AA] mb-6">
            The organizer has not enabled public viewing of live scores for this competition.
          </p>
          <p className="text-[#52525B] text-sm">
            Check back later or ask the organizer for access.
          </p>
        </div>
      </div>
    );
  }

  const { competition, festival, current_heat, top_leaders, top_followers } = liveData || {};

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-[#FFD700]" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-[#C0C0C0]" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-[#CD7F32]" />;
    return <span className="w-6 h-6 flex items-center justify-center text-[#52525B] font-bold">{rank}</span>;
  };

  return (
    <div className="min-h-screen bg-[#09090b] overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FFD700]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#FF007F]/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Trophy className="w-10 h-10 text-[#FFD700]" />
          <div>
            <h1 className="font-['Playfair_Display'] text-2xl font-bold text-white">
              {festival?.name || "Live Leaderboard"}
            </h1>
            <p className="text-[#A1A1AA]">{competition?.category}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={fetchLiveData}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-5 h-5 text-[#A1A1AA]" />
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            data-testid="fullscreen-btn"
          >
            <Monitor className="w-5 h-5 text-[#A1A1AA]" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-8 pb-8">
        {/* Current Heat Banner */}
        {current_heat && (
          <motion.div
            className="mb-8 text-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-[#FF007F]/20 rounded-2xl border border-[#FF007F]/30">
              <span className="w-3 h-3 bg-[#FF007F] rounded-full animate-pulse"></span>
              <span className="text-[#FF007F] text-2xl font-bold">
                NOW: HEAT {current_heat.heat_number}
              </span>
              <span className="text-white/60">|</span>
              <span className="text-white text-xl">
                Round {current_heat.round_number}
              </span>
            </div>
            {current_heat.pairs && current_heat.pairs.length > 0 && (
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                {current_heat.pairs.slice(0, 5).map((pair) => (
                  <div key={`${pair.leader_id}-${pair.follower_id}`} className="px-4 py-2 bg-white/5 rounded-lg text-sm">
                    <span className="text-[#FFD700]">{pair.leader_name}</span>
                    <span className="text-[#52525B] mx-2">&</span>
                    <span className="text-[#FF007F]">{pair.follower_name}</span>
                  </div>
                ))}
                {current_heat.pairs.length > 5 && (
                  <div className="px-4 py-2 bg-white/5 rounded-lg text-sm text-[#A1A1AA]">
                    +{current_heat.pairs.length - 5} more
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Rankings Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Leaders */}
          <div className="glass rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-[#FFD700]"></div>
              <h2 className="font-['Playfair_Display'] text-xl font-bold text-white">Leaders</h2>
            </div>
            <div className="p-4 space-y-2">
              <AnimatePresence mode="popLayout">
                {top_leaders?.map((dancer, index) => (
                  <motion.div
                    key={dancer.dancer_id}
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center justify-between p-4 rounded-xl ${
                      index === 0 ? 'bg-[#FFD700]/10 border border-[#FFD700]/30' : 'bg-white/5'
                    }`}
                    data-testid={`leader-${dancer.dancer_id}`}
                  >
                    <div className="flex items-center gap-4">
                      {getRankIcon(dancer.rank || index + 1)}
                      <div>
                        <p className="text-white font-medium">{dancer.name}</p>
                        <p className="text-[#52525B] text-sm">{dancer.country}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-['Playfair_Display'] text-2xl font-bold text-[#FFD700]">
                        {(dancer.combined_score ?? dancer.average_score ?? 0).toFixed(1)}
                      </p>
                      <p className="text-[#52525B] text-xs">
                        {(dancer.audience_normalized !== undefined && dancer.total_audience_members > 0) ? `J:${(dancer.average_score ?? 0).toFixed(1)} A:${(dancer.audience_normalized ?? 5).toFixed(1)}` : 'avg score'}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              {(!top_leaders || top_leaders.length === 0) && (
                <div className="text-center py-8 text-[#52525B]">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No rankings yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Followers */}
          <div className="glass rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-[#FF007F]"></div>
              <h2 className="font-['Playfair_Display'] text-xl font-bold text-white">Followers</h2>
            </div>
            <div className="p-4 space-y-2">
              <AnimatePresence mode="popLayout">
                {top_followers?.map((dancer, index) => (
                  <motion.div
                    key={dancer.dancer_id}
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center justify-between p-4 rounded-xl ${
                      index === 0 ? 'bg-[#FF007F]/10 border border-[#FF007F]/30' : 'bg-white/5'
                    }`}
                    data-testid={`follower-${dancer.dancer_id}`}
                  >
                    <div className="flex items-center gap-4">
                      {getRankIcon(dancer.rank || index + 1)}
                      <div>
                        <p className="text-white font-medium">{dancer.name}</p>
                        <p className="text-[#52525B] text-sm">{dancer.country}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-['Playfair_Display'] text-2xl font-bold text-[#FF007F]">
                        {(dancer.combined_score ?? dancer.average_score ?? 0).toFixed(1)}
                      </p>
                      <p className="text-[#52525B] text-xs">
                        {(dancer.audience_normalized !== undefined && dancer.total_audience_members > 0) ? `J:${(dancer.average_score ?? 0).toFixed(1)} A:${(dancer.audience_normalized ?? 5).toFixed(1)}` : 'avg score'}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              {(!top_followers || top_followers.length === 0) && (
                <div className="text-center py-8 text-[#52525B]">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No rankings yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 py-4 text-center">
        <a href="/terms" className="text-[#52525B] text-sm hover:text-[#A1A1AA] transition-colors">Terms of Use</a>
        <span className="text-[#27272a] mx-2">|</span>
        <span className="text-[#52525B] text-sm">JackAndJillScore — All rights reserved.</span>
      </footer>
    </div>
  );
}
